import { Loader2, Trash2 } from "lucide-react";
import { type KeyboardEvent, useId, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Highlight } from "@/components/highlight";
import { RowAction } from "@/components/row-action";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Popover, PopoverAnchor, PopoverContent } from "@/components/ui/popover";
import {
  type RoadmapComment,
  type RoadmapItem,
  type RoadmapPerson,
  useAddRoadmapComment,
  useDeleteRoadmapComment,
  useRoadmapComments,
} from "@/lib/api";
import { apiErrorMessage, inlineFailure } from "@/lib/api-errors";
import { formatDateTime, formatNumber } from "@/lib/format";
import {
  byEmail,
  firstNameLabel,
  type NamedPerson,
  normalEmail,
  personInitials,
  personLabel,
} from "@/lib/people";
import { cn } from "@/lib/utils";

import { DetailsCard, type DetailsContext, Failure, useFocusBack } from "./details-parts";
import { TEXTAREA_CLASS } from "./item-form";
import {
  commentParts,
  insertMention,
  mentionCandidates,
  mentionLabel,
  mentionOptions,
  mentionQueryAt,
  type PickedMention,
  sentMentions,
} from "./mentions";
import { StepButtons } from "./step-buttons";

/** The longest comment the server takes. */
const COMMENT_MAX = 2000;

/** A comment that failed to post or to go, worded at render so a language switch re-words it. */
interface CommentFailure {
  error: unknown;
  action: "addRoadmapComment" | "deleteRoadmapComment";
}

interface CommentsCardProps {
  item: RoadmapItem;
  overview: DetailsContext;
  pastMonth: boolean;
}

/**
 * The item's comments, oldest first: who wrote each and when, and its text
 * with every mention marked. Under them a box to comment, where "@" lists the
 * people to mention. Whoever wrote a comment, and a developer, may delete it,
 * asked first. A done item takes no new comment, and nothing changes from a
 * past month. No one is notified yet: a mention is kept for later.
 */
export function CommentsCard({ item, overview, pastMonth }: CommentsCardProps) {
  const { t } = useTranslation();
  const comments = useRoadmapComments(item.id);
  // Kept here, not in the box or the row: a refusal may remove either.
  const [failure, setFailure] = useState<CommentFailure | null>(null);
  const list = useRef<HTMLDivElement>(null);
  const people = byEmail(overview.people);
  const thread = comments.data ?? [];
  const count = comments.data?.length ?? item.comment_count;
  const done = item.status === "done";

  return (
    <DetailsCard
      title={t("roadmap.comments.card")}
      note={count > 0 ? formatNumber(count) : undefined}
      section="comments"
    >
      <div className="space-y-3 text-sm">
        <div ref={list} tabIndex={-1} className="focus:outline-none">
          {comments.isLoading ? (
            <p className="text-muted">{t("common.loadingEllipsis")}</p>
          ) : comments.isError && !comments.data ? (
            <p className="text-destructive">
              {t("roadmap.comments.loadError")}{" "}
              <button
                type="button"
                onClick={() => void comments.refetch()}
                className="text-accent font-medium hover:underline"
              >
                {t("common.tryAgain")}
              </button>
            </p>
          ) : thread.length === 0 ? (
            <p className="text-muted">{t("roadmap.comments.none")}</p>
          ) : (
            <ol className="divide-border -my-2 divide-y" data-comments="">
              {thread.map((comment) => (
                <CommentRow
                  key={comment.id}
                  comment={comment}
                  people={people}
                  canDelete={comment.can_delete && !pastMonth}
                  onStart={() => setFailure(null)}
                  // The row left: the focus goes to the thread, never to the page's body.
                  onDeleted={() => list.current?.focus()}
                  onError={(error) =>
                    setFailure(inlineFailure({ error, action: "deleteRoadmapComment" }))
                  }
                />
              ))}
            </ol>
          )}
        </div>
        {failure && <Failure>{apiErrorMessage(failure.error, failure.action)}</Failure>}
        {done ? (
          <p className="text-muted text-xs">{t("roadmap.comments.closed")}</p>
        ) : (
          !pastMonth && (
            <CommentComposer
              itemId={item.id}
              candidates={mentionCandidates(overview.people, overview.viewer.email)}
              onStart={() => setFailure(null)}
              onError={(error) => setFailure(inlineFailure({ error, action: "addRoadmapComment" }))}
            />
          )
        )}
      </div>
    </DetailsCard>
  );
}

interface CommentRowProps {
  comment: RoadmapComment;
  people: ReadonlyMap<string, NamedPerson>;
  /** Its author, or a developer, outside a past month. */
  canDelete: boolean;
  onStart: () => void;
  onDeleted: () => void;
  onError: (error: unknown) => void;
}

/**
 * One comment: its author's avatar, their first name (`firstNameLabel`) and
 * when, the delete action, and its text.
 */
function CommentRow({ comment, people, canDelete, onStart, onDeleted, onError }: CommentRowProps) {
  const { t } = useTranslation();
  const remove = useDeleteRoadmapComment();
  const [asking, setAsking] = useState(false);
  // Annuleren under the question hands the focus back to the delete action.
  const deleteButton = useFocusBack(asking);
  const person = people.get(normalEmail(comment.author_email)) ?? {
    email: comment.author_email,
  };
  const author = firstNameLabel(people.values())(person);

  const submit = async () => {
    if (remove.isPending) {
      return;
    }
    onStart();
    try {
      await remove.mutateAsync({ itemId: comment.item_id, commentId: comment.id });
      onDeleted();
    } catch (error) {
      setAsking(false);
      onError(error);
    }
  };

  return (
    <li className="py-2" data-comment={comment.id}>
      <div className="flex items-start gap-2">
        {/* Level with the name: the name's line sits 6 px down, as high as the delete action. */}
        <Avatar size="sm" aria-hidden="true" className="mt-1">
          <AvatarFallback className="group-data-[size=sm]/avatar:text-[0.625rem]">
            {personInitials(person)}
          </AvatarFallback>
        </Avatar>
        <p className="min-w-0 flex-1 pt-1.5">
          <span className="text-foreground font-medium wrap-anywhere">{author}</span>
          <time dateTime={comment.created_at} className="text-muted ml-2 text-xs tabular-nums">
            {formatDateTime(comment.created_at)}
          </time>
        </p>
        {canDelete && !asking && (
          <RowAction
            ref={deleteButton}
            label={t("roadmap.comments.delete", { person: author })}
            icon={Trash2}
            destructive
            onClick={() => setAsking(true)}
          />
        )}
      </div>
      {/* Under the name, past the avatar. */}
      <p className="text-foreground pl-8 wrap-anywhere whitespace-pre-wrap">
        <CommentText comment={comment} people={people} />
      </p>
      {asking && (
        <div className="mt-2 space-y-2 pl-8" data-section="delete-comment">
          <p>{t("roadmap.comments.deleteBody")}</p>
          <StepButtons
            onBack={() => setAsking(false)}
            onConfirm={() => void submit()}
            confirm={t("common.delete")}
            pending={remove.isPending}
            destructive
            autoFocusBack
          />
        </div>
      )}
    </li>
  );
}

interface CommentTextProps {
  comment: Pick<RoadmapComment, "body" | "mentions">;
  people: ReadonlyMap<string, NamedPerson>;
}

/** A comment's text, with each mention of someone it mentions marked in the theme's light blue. */
export function CommentText({ comment, people }: CommentTextProps) {
  return commentParts(comment.body, comment.mentions, people).map((part, index) =>
    part.kind === "mention" ? (
      <span
        key={index}
        // The negative margin offsets the padding, so marking a mention never shifts the text.
        className="bg-accent-light text-primary -mx-0.5 rounded-sm px-0.5 font-medium"
        data-mention=""
      >
        {part.text}
      </span>
    ) : (
      part.text
    ),
  );
}

interface CommentComposerProps {
  itemId: string;
  /** The people "@" lists: those who reach the roadmap, the viewer left out. */
  candidates: readonly RoadmapPerson[];
  /** A post starts: an earlier failure no longer holds. */
  onStart: () => void;
  onError: (error: unknown) => void;
}

/**
 * The box to comment in, and Plaats. Typing "@" opens a list of the people
 * to mention under the box, filtered as one types on; the arrows move in it,
 * Enter or Tab picks, Escape closes it. A pick puts "@Name" in the text, and
 * the comment mentions whoever is still named so when it is sent
 * (`sentMentions`). Ctrl+Enter (⌘+Enter on a Mac) posts too.
 */
function CommentComposer({ itemId, candidates, onStart, onError }: CommentComposerProps) {
  const { t } = useTranslation();
  const ids = useId();
  const add = useAddRoadmapComment();
  const box = useRef<HTMLTextAreaElement>(null);
  const [text, setText] = useState("");
  const [caret, setCaret] = useState(0);
  const [picked, setPicked] = useState<PickedMention[]>([]);
  // The "@" whose list was closed (Escape, a pick): it stays closed until another "@" is typed.
  const [dismissed, setDismissed] = useState<number | null>(null);
  const [active, setActive] = useState(0);
  // The caret after a pick, set in the box once the new text is in it.
  const pendingCaret = useRef<number | null>(null);

  const at = mentionQueryAt(text, caret);
  const options = at && at.start !== dismissed ? mentionOptions(candidates, at.query) : [];
  const open = options.length > 0;
  const current = open ? Math.min(active, options.length - 1) : null;
  const optionId = (index: number) => `${ids}-mention-${index}`;
  const filled = text.trim() !== "";

  useLayoutEffect(() => {
    if (pendingCaret.current !== null && box.current) {
      box.current.setSelectionRange(pendingCaret.current, pendingCaret.current);
      pendingCaret.current = null;
    }
  }, [text]);

  const follow = (nextText: string, nextCaret: number) => {
    const next = mentionQueryAt(nextText, nextCaret);
    if (!next || next.start !== at?.start || next.query !== at.query) {
      setActive(0);
    }
    if (!next || next.start !== dismissed) {
      setDismissed(null);
    }
    setText(nextText);
    setCaret(nextCaret);
  };

  const pick = (person: RoadmapPerson) => {
    // While a comment posts, the box is read-only, so its text is what gets cleared.
    if (!at || add.isPending) {
      return;
    }
    const next = insertMention(text, at, person);
    setPicked((before) => [...before, { email: person.email, label: mentionLabel(person) }]);
    setDismissed(at.start);
    setActive(0);
    setText(next.text);
    setCaret(next.caret);
    pendingCaret.current = next.caret;
  };

  const submit = async () => {
    const body = text.trim();
    if (body === "" || add.isPending) {
      return;
    }
    onStart();
    try {
      await add.mutateAsync({
        itemId,
        comment: { body, mentions: sentMentions(body, picked) },
      });
      setText("");
      setCaret(0);
      setPicked([]);
      setDismissed(null);
      // Back in the box for the next comment; Plaats goes disabled while it is empty.
      box.current?.focus();
    } catch (error) {
      onError(error);
    }
  };

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (open && current !== null) {
      if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        event.preventDefault();
        const step = event.key === "ArrowDown" ? 1 : -1;
        setActive((current + step + options.length) % options.length);
        return;
      }
      if ((event.key === "Enter" || event.key === "Tab") && !event.shiftKey) {
        const person = options[current];
        if (person) {
          event.preventDefault();
          pick(person);
        }
        return;
      }
    }
    if (event.key === "Enter" && (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
      void submit();
    }
  };

  return (
    <div className="space-y-2">
      <Popover
        open={open}
        onOpenChange={(next) => {
          if (!next && at) {
            setDismissed(at.start);
          }
        }}
      >
        <PopoverAnchor asChild>
          <textarea
            ref={box}
            id={`${ids}-comment`}
            aria-label={t("roadmap.comments.input")}
            placeholder={t("roadmap.comments.placeholder")}
            aria-autocomplete="list"
            aria-controls={open ? `${ids}-mentions` : undefined}
            aria-activedescendant={current === null ? undefined : optionId(current)}
            value={text}
            readOnly={add.isPending}
            aria-busy={add.isPending || undefined}
            maxLength={COMMENT_MAX}
            rows={3}
            onChange={(event) =>
              follow(event.target.value, event.target.selectionStart ?? event.target.value.length)
            }
            onSelect={(event) => setCaret(event.currentTarget.selectionStart ?? 0)}
            onKeyDown={onKeyDown}
            className={cn(TEXTAREA_CLASS, "min-h-20")}
            data-comment-box=""
          />
        </PopoverAnchor>
        <PopoverContent
          align="start"
          className="w-72 max-w-[calc(100vw-2rem)] p-1"
          // The focus stays in the box, so typing goes on while the list shows.
          onOpenAutoFocus={(event) => event.preventDefault()}
          onCloseAutoFocus={(event) => event.preventDefault()}
          onInteractOutside={(event) => {
            if (event.target instanceof Node && box.current?.contains(event.target)) {
              event.preventDefault();
            }
          }}
        >
          <ul id={`${ids}-mentions`} role="listbox" aria-label={t("roadmap.comments.mentionList")}>
            {options.map((person, index) => (
              <li
                key={person.email}
                id={optionId(index)}
                role="option"
                aria-selected={index === current}
                // Keep the focus in the box, so typing goes on after a click.
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => pick(person)}
                onMouseMove={() => setActive(index)}
                className={cn(
                  "flex cursor-default flex-col rounded-sm px-2 py-1.5 text-sm pointer-coarse:py-2.5",
                  index === current && "bg-accent-light",
                )}
                data-mention-option=""
              >
                <span className="text-foreground truncate font-medium">
                  <Highlight text={personLabel(person)} query={at?.query ?? ""} />
                </span>
                {person.name?.trim() && (
                  <span className="text-muted truncate text-xs">
                    <Highlight text={person.email} query={at?.query ?? ""} />
                  </span>
                )}
              </li>
            ))}
          </ul>
        </PopoverContent>
      </Popover>
      <div className="flex justify-end">
        <Button
          type="button"
          size="sm"
          onClick={() => void submit()}
          disabled={!filled && !add.isPending}
          // Never `disabled` while it posts: that would drop the focus.
          aria-disabled={add.isPending || undefined}
          className="aria-disabled:cursor-wait"
          data-comment-action="post"
        >
          {add.isPending && <Loader2 aria-hidden="true" className="size-4 animate-spin" />}
          {t("roadmap.comments.post")}
        </Button>
      </div>
    </div>
  );
}
